import type { ReactNode } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@/shared/ui';
import {
  getPasswordPolicyChecks,
  PASSWORD_MAX_LENGTH,
  type PasswordPolicyRules,
} from '@/shared/utils/validation';

const PasswordRuleList = ({
  children,
  columns = 1,
}: {
  children: ReactNode;
  columns?: 1 | 2;
}) => (
  <Box
    component="ul"
    sx={{
      m: 0,
      p: 0,
      listStyle: 'none',
      display: 'grid',
      gridTemplateColumns: columns === 2 ? '1fr 1fr' : '1fr',
      columnGap: 1,
      rowGap: 0.5,
    }}
  >
    {children}
  </Box>
);

/** One live rule row: empty circle until met, then a green check. */
export const PasswordRuleRow = ({
  met,
  children,
}: {
  met: boolean;
  children: ReactNode;
}) => (
  <Box
    component="li"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      minWidth: 0,
      color: met ? 'success.main' : 'text.secondary',
    }}
  >
    {met ? (
      <CheckCircleIcon color="success" sx={{ fontSize: 18, flexShrink: 0 }} />
    ) : (
      <RadioButtonUncheckedIcon color="disabled" sx={{ fontSize: 18, flexShrink: 0 }} />
    )}
    <Typography variant="body2" color="inherit">
      {children}
    </Typography>
  </Box>
);

/**
 * Live password-policy checklist under a new-password field.
 * Met rules get a green check; unmet rules stay as an empty circle.
 */
const PasswordPolicyHints = ({
  password,
  policy,
}: {
  password: string;
  policy: PasswordPolicyRules;
}) => {
  const { t } = useTranslation('identity');
  const checks = getPasswordPolicyChecks(password, policy);

  return (
    <PasswordRuleList columns={2}>
      {checks.map((check) => (
        <PasswordRuleRow key={check.id} met={check.met}>
          {check.id === 'min'
            ? t('changePassword.rules.min', {
                min: policy.passwordMinLength,
                max: PASSWORD_MAX_LENGTH,
              })
            : t(`changePassword.rules.${check.id}`)}
        </PasswordRuleRow>
      ))}
    </PasswordRuleList>
  );
};

/** Confirm-password row in the same checklist visual language. */
export const PasswordMatchHint = ({
  password,
  confirmPassword,
}: {
  password: string;
  confirmPassword: string;
}) => {
  const { t } = useTranslation('identity');
  const matched = confirmPassword.length > 0 && confirmPassword === password;

  return (
    <PasswordRuleList>
      <PasswordRuleRow met={matched}>{t('changePassword.rules.match')}</PasswordRuleRow>
    </PasswordRuleList>
  );
};

export default PasswordPolicyHints;
